import { existsSync, readdirSync } from "node:fs";
import { basename, join } from "node:path";

import { normalizeAthenaAuthConfig } from "../auth/config.ts";
import type { AthenaRuntimeDiscoveryDiagnostics } from "../gateway/discovery-types.ts";
import { findGeneratorConfigPath } from "../generator/config.ts";
import { DEFAULT_MIGRATIONS_DIRECTORY } from "../migrations/constants.ts";
import type { AthenaClientInternals } from "../runtime/client-internals.ts";
import {
	resolveDatabaseUri,
	toAthenaRuntimeDiagnostics,
} from "../runtime/resolve.ts";

const MIGRATION_SQL_RE = /^\d+_.+\.sql$/i;
const MAX_AUTH_WARNINGS = 8;

function hasAttachedModels(models: unknown): boolean {
	if (models == null) {
		return false;
	}
	if (Array.isArray(models)) {
		return models.length > 0;
	}
	if (typeof models === "object") {
		return Object.keys(models).length > 0;
	}
	return true;
}

function countLocalMigrationFiles(cwd: string, directory: string): number {
	const absolute = join(cwd, directory);
	if (!existsSync(absolute)) {
		return 0;
	}
	try {
		return readdirSync(absolute).filter((name) => MIGRATION_SQL_RE.test(name))
			.length;
	} catch {
		return 0;
	}
}

export function buildAthenaRuntimeDiscoveryDiagnostics(
	internals: AthenaClientInternals,
): AthenaRuntimeDiscoveryDiagnostics {
	const snapshot = toAthenaRuntimeDiagnostics(internals.plan);
	const auth = normalizeAthenaAuthConfig(internals.config.auth);
	const cwd = process.cwd();
	const generatorPath = findGeneratorConfigPath(cwd);
	const migrationsDirectory = DEFAULT_MIGRATIONS_DIRECTORY;
	const migrationsDirectoryFound = existsSync(join(cwd, migrationsDirectory));
	return {
		auth: snapshot.auth,
		config: {
			autoMigrate: auth.autoMigrate,
			authWarnings: auth.warnings.slice(0, MAX_AUTH_WARNINGS),
			databaseConfigured: Boolean(resolveDatabaseUri(internals.config)),
			generatorConfigFile: generatorPath ? basename(generatorPath) : null,
			localMigrationFiles: countLocalMigrationFiles(cwd, migrationsDirectory),
			migrationsDirectory,
			migrationsDirectoryFound,
			modelsAttached: hasAttachedModels(internals.config.models),
		},
		database: snapshot.database,
		passkey: {
			authenticatorAttachment: auth.passkey.registration.authenticatorAttachment,
			configured: auth.passkeyConfigured,
			enabled: auth.passkey.enabled,
			onboardingEnabled: auth.passkey.onboardingEnabled,
			origins: [...auth.passkey.origins],
			relatedOrigins: [...auth.passkey.relatedOrigins],
			residentKey: auth.passkey.registration.residentKey,
			rpId: auth.passkey.rpId,
			rpName: auth.passkey.rpName,
			timeoutMs: auth.passkey.challengeTtlSeconds * 1000,
			userVerification: auth.passkey.authentication.userVerification,
		},
		runtime: snapshot.runtime,
		storage: snapshot.storage,
	};
}
